import { create } from "zustand";

// --- Types ---

export interface Toast {
	id: string;
	type: "pending" | "success" | "error";
	title: string;
	message: string;
	hash?: `0x${string}`;
	timestamp: number;
}

interface TransactionStore {
	toasts: Toast[];
	addToast: (toast: Omit<Toast, "id" | "timestamp">) => string;
	updateToast: (id: string, updates: Partial<Toast>) => void;
	removeToast: (id: string) => void;
	clearAll: () => void;
}

// --- Auto-dismiss durations (ms) ---

const DISMISS_DELAY: Record<Toast["type"], number | null> = {
	pending: null,
	success: 5_000,
	error: 8_000,
};

// Track active timers so they can be cleared on update or manual dismiss
const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleDismiss(id: string, type: Toast["type"]): void {
	// Clear any existing timer for this toast
	const existing = dismissTimers.get(id);
	if (existing) clearTimeout(existing);

	const delay = DISMISS_DELAY[type];
	if (delay === null) return;

	const timer = setTimeout(() => {
		useTransactionStore.getState().removeToast(id);
		dismissTimers.delete(id);
	}, delay);

	dismissTimers.set(id, timer);
}

// --- Store ---

export const useTransactionStore = create<TransactionStore>((set) => ({
	toasts: [],

	addToast: (toast) => {
		const id = crypto.randomUUID();
		const newToast: Toast = { ...toast, id, timestamp: Date.now() };

		set((state) => ({ toasts: [...state.toasts, newToast] }));
		scheduleDismiss(id, toast.type);

		return id;
	},

	updateToast: (id, updates) => {
		set((state) => ({
			toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...updates } : t)),
		}));

		// Reschedule dismiss if type changed
		if (updates.type) {
			scheduleDismiss(id, updates.type);
		}
	},

	removeToast: (id) => {
		const timer = dismissTimers.get(id);
		if (timer) {
			clearTimeout(timer);
			dismissTimers.delete(id);
		}

		set((state) => ({
			toasts: state.toasts.filter((t) => t.id !== id),
		}));
	},

	clearAll: () => {
		for (const timer of dismissTimers.values()) {
			clearTimeout(timer);
		}
		dismissTimers.clear();

		set({ toasts: [] });
	},
}));
